import React, { useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { assets } from "../../../../assets/assets";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../../../context/AppContext";
import DateUtils from "../../../../utils/DateUtils";
import { Service, Slot } from "../../../../dto/interfaces";

interface AppointmentConfirmationProps {
  service: Service | null;
  date: string;
  slot: Slot | null;
  name: string;
  phone: { countryCode: string; phoneNumber: string };
  email: string;
  isSuccess: boolean;
  id: string;
  alias: string;
}

const AppointmentConfirmation: React.FC<AppointmentConfirmationProps> = ({
  service,
  date,
  slot,
  name,
  phone,
  email,
  isSuccess,
  id,
  alias,
}) => {
  const { token } = useContext(AppContext);
  const navigate = useNavigate();
  useEffect(() => {
    if (isSuccess) {
      toast.success("Appointment Scheduled Successfully");
    }
  }, [isSuccess]);

  const handleNavigation = useCallback(() => {
    navigate(`/${alias}/appointment/${id}/${token}`);
  }, [alias ,id ,token]);

  return (
    <div className="bg-white p-2">
      {isSuccess ? (
        <div className="text-center space-y-2 rounded-lg">
          <h2 className="text-xl font-bold text-green-700">
            Appointment Confirmed!
          </h2>
          <p className="text-gray-600">
            An email confirmation has been sent to your inbox.
          </p>
          <div className="bg-green-100 p-4 rounded-lg">
            <p className="text-lg font-semibold">{service?.name}</p>
            <p className="text-gray-600">
              {DateUtils.getHumanReadableDate(date)} at{" "}
              {DateUtils.getHumanReadableTime(slot?.start || "")}
            </p>
          </div>
          <div className="flex justify-center items-center pt-2">
            <button
              className="flex items-center gap-2 bg-primary px-4 py-2 rounded-md hover:bg-gray-700 text-white justify-center"
              onClick={handleNavigation}
            >
              Manage Your Appointment
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-800">
              Appointment Details
            </h2>
            <p className="text-gray-500 mt-2">
              Please review your appointment information
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg space-y-4">
              <div className="flex items-center gap-3">
                <img src={assets.profile_icon} alt="" width={18} />
                <div>
                  <p className="text-sm text-gray-500">Client Name</p>
                  <p className="text-lg font-medium text-gray-800">{name}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <img src={assets.call_icon} alt="" width={20} />
                <div>
                  <p className="text-sm text-gray-500">Phone Number</p>
                  <p className="text-lg font-medium text-gray-800">
                    {phone.countryCode} {phone.phoneNumber}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <img src={assets.mail_icon} alt="" width={20} />
                <div>
                  <p className="text-sm text-gray-500">Email Address</p>
                  <p className="text-lg font-medium text-gray-800">{email}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg space-y-4">
              <div className="flex items-center gap-3">
                <img src={assets.paperroll_icon} alt="" width={20} />
                <div>
                  <p className="text-sm text-gray-500">Service</p>
                  <p className="text-lg font-medium text-gray-800">
                    {service?.name}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <img src={assets.card_icon} alt="" width={20} />
                <div>
                  <p className="text-sm text-gray-500">Service Fee</p>
                  <p className="text-lg font-medium text-gray-800">
                    ₹ {service?.fees.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg space-y-4">
              <div className="flex items-center gap-3">
                <img src={assets.calender_icon} alt="" width={20} />
                <div>
                  <p className="text-sm text-gray-500">Appointment Date</p>
                  <p className="text-lg font-medium text-gray-800">
                    {DateUtils.getHumanReadableDate(date)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <img src={assets.clock_icon} alt="" width={20} />
                <div>
                  <p className="text-sm text-gray-500">Time Slot</p>
                  <p className="text-lg font-medium text-gray-800">
                    {DateUtils.getHumanReadableTime(slot?.start || "")}
                    {" - "} {DateUtils.getHumanReadableTime(slot?.end || "")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AppointmentConfirmation;
